import { CompilationManifest } from '@msdk/definitions'
import { useRecoilValue, useRecoilValueLoadable } from 'recoil'

import { $baseMetadata, $compilationManifest } from './selectors'

/**
 * The function `useEntityBaseMetadata` returns the base metadata for a given entity name.
 * @param {string} entityName - A string representing the name of the entity for which you want to
 * retrieve the base metadata.
 * @returns the metadata for the specified entity name.
 */
export function useEntityBaseMetadata(entityName: string) {
  const metadata = useRecoilValue($baseMetadata)
  const entityMetadata = metadata.entities.find((e) => e.name === entityName)
  return entityMetadata
}

/**
 * The function `useEntityBaseMetadataSync` returns the metadata of a specific entity based on its
 * name, using Recoil's `useRecoilValueLoadable` hook.
 * @param {string} entityName - The `entityName` parameter is a string that represents the name of the
 * entity for which you want to retrieve the metadata.
 * @returns the metadata of the specified entity if it is available. If the metadata is not available,
 * it returns undefined. If there is an error while loading the metadata, it throws the error.
 */
export function useEntityBaseMetadataSync(entityName: string) {
  const result = useRecoilValueLoadable($baseMetadata)
  switch (result.state) {
    case 'hasValue': {
      const metadata = result.contents
      const entityMetadata = metadata.entities.find((e) => e.name === entityName)
      return entityMetadata
    }
    case 'hasError':
      throw result.contents
    case 'loading':
      return undefined
  }
}

/**
 * The function `useFormBaseMetadata` retrieves the metadata for a specific form associated with an
 * entity.
 * @param {string} entityName - The `entityName` parameter is a string that represents the name of an
 * entity. An entity is a data object or concept that is represented in the application. Examples of
 * entities could be "User", "Product", "Order", etc.
 * @param {string} formName - The `formName` parameter is a string that represents the name of a form.
 * @returns the `entityMetadata` object.
 */
export function useFormBaseMetadata(entityName: string, formName: string) {
  const metadata = useRecoilValue($baseMetadata)
  let entityMetadata = metadata.entities.find((e) => e.name === entityName)
  const formMetadata = entityMetadata?.formEntries.find((v) => v.name === formName)
  if (!formMetadata) {
    entityMetadata = metadata.externalEntities.find(
      (e) => e.name === entityName && e.formEntries.find((f) => f.name === formName),
    )
  }
  return entityMetadata
}

/**
 * The function `useFormBaseMetadataSync` retrieves form metadata based on the provided entity name and
 * form name.
 * @param {string} entityName - The `entityName` parameter is a string that represents the name of an
 * entity.
 * @param {string} formName - The `formName` parameter is a string that represents the name of a form.
 * @returns the `entityMetadata` object if it exists, otherwise it returns `undefined`.
 */
export function useFormBaseMetadataSync(entityName: string, formName: string) {
  const result = useRecoilValueLoadable($baseMetadata)
  switch (result.state) {
    case 'hasValue': {
      const metadata = result.contents
      let entityMetadata = metadata.entities.find((e) => e.name === entityName)
      const formMetadata = entityMetadata?.formEntries.find((v) => v.name === formName)
      if (!formMetadata) {
        entityMetadata = metadata.externalEntities.find(
          (e) => e.name === entityName && e.formEntries.find((f) => f.name === formName),
        )
      }
      return entityMetadata
    }
    case 'hasError':
      throw result.contents
    case 'loading':
      return undefined
  }
}

/**
 * The function `useViewBaseMetadata` retrieves the metadata for a specific view in an entity.
 * @param {string} entityName - The `entityName` parameter is a string that represents the name of an
 * entity. It is used to find the metadata for the specified entity.
 * @param {string} viewName - The `viewName` parameter is a string that represents the name of a view.
 * @returns the entity metadata for the specified entity name and view name.
 */
export function useViewBaseMetadata(entityName: string, viewName: string) {
  const metadata = useRecoilValue($baseMetadata)
  let entityMetadata = metadata.entities.find((e) => e.name === entityName)
  const viewMetadata = entityMetadata?.viewEntries.find((v) => v.name === viewName)
  if (!viewMetadata) {
    entityMetadata = metadata.externalEntities.find(
      (e) => e.name === entityName && e.viewEntries.findIndex((v) => v.name === viewName) !== -1,
    )
  }
  return entityMetadata
}
/**
 * The function `useViewBaseMetadataSync` retrieves the metadata for a specific entity and view from a
 * source and returns the entity metadata if found.
 * @param {string} entityName - The `entityName` parameter is a string that represents the name of an
 * entity. It is used to filter and retrieve the metadata for a specific entity.
 * @param {string} viewName - The `viewName` parameter is a string that represents the name of a view.
 * @returns the entity metadata for the specified entity name and view name.
 */

export function useViewBaseMetadataSync(entityName: string, viewName: string) {
  const result = useRecoilValueLoadable($baseMetadata)
  switch (result.state) {
    case 'hasValue': {
      const metadata = result.contents
      let entityMetadata = metadata.entities.find((e) => e.name === entityName)
      const viewMetadata = entityMetadata?.viewEntries.find((v) => v.name === viewName)
      if (!viewMetadata) {
        entityMetadata = metadata.externalEntities.find(
          (e) => e.name === entityName && e.viewEntries.findIndex((v) => v.name === viewName) !== -1,
        )
      }
      return entityMetadata
    }
    case 'hasError':
      throw result.contents
    case 'loading':
      return undefined
  }
}

/**
 * The function `useModuleBaseMetadata` retrieves metadata for a specific entity and module name from a
 * Recoil state.
 * @param {string} entityName - The `entityName` parameter is a string that represents the name of an
 * entity. It is used to find the corresponding metadata for that entity.
 * @param {string} moduleName - The `moduleName` parameter is a string that represents the name of a
 * module.
 * @returns the `entityMetadata` object.
 */
export function useModuleBaseMetadata(entityName: string, moduleName: string) {
  const metadata = useRecoilValue($baseMetadata)

  const metadataForForm = metadata.entities.find(
    (e) => e.name === entityName && e.formEntries.find((m) => m.name === moduleName),
  )

  const metadataForView = metadata.entities.find(
    (e) => e.name === entityName && e.viewEntries.find((m) => m.name === moduleName),
  )

  let entityMetadata = metadataForForm ?? metadataForView

  if (!entityMetadata) {
    const externalForForm = metadata.externalEntities.find(
      (e) => e.name === entityName && e.formEntries.find((m) => m.name === moduleName),
    )

    const externalForView = metadata.externalEntities.find(
      (e) => e.name === entityName && e.viewEntries.find((m) => m.name === moduleName),
    )

    entityMetadata = externalForForm ?? externalForView
  }

  return entityMetadata
}

/**
 * The function `useBaseMetadataForLinkedEntities` retrieves metadata for a specific entity and module name (if given)
 * from a Recoil state.
 * @param {string} entityName - The `entityName` parameter is a string that represents the name of an
 * entity. It is used to find the corresponding metadata for that entity.
 * @param {string} moduleName - The `moduleName` parameter is a string that represents the name of a
 * module.
 * @returns the `entityMetadata` object.
 */
export function useBaseMetadataForLinkedEntities(entityName: string, moduleName: string) {
  const entityBaseMetadata = useEntityBaseMetadata(entityName)
  const moduleBaseMetadata = useModuleBaseMetadata(entityName, moduleName)

  /**
   * if linked there is no moduleName
   */
  return moduleName ? moduleBaseMetadata : entityBaseMetadata
}

/**
 * The function returns the compilation manifest obtained from a Recoil state.
 * @returns the value of the `manifest` variable, which is of type `CompilationManifest`.
 */
export function useCompilationManifest(): CompilationManifest {
  const manifest = useRecoilValue($compilationManifest)
  return manifest
}
